import { z } from "zod";
import { useMutation, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { getOneProductKey } from "../products/useGetOneProduct";
import { getProductScoresKey } from "../olapScores/useGetProductScores";

const baseUrl = "/api/product-organization-activity-datas" as const;

type UpsertProductOrganizationActivityData = {
    productId?: number;
};

export const useUpsertProductOrganizationActivityData = (options: UpsertProductOrganizationActivityData) => {
    const queryClient = useQueryClient();
    const { selectedCompany } = useFoodpilotContext();

    return useMutation({
        mutationFn: async (activityData: ProductOrganizationActivityDataWrite) => {
            if (selectedCompany === null) {
                throw new Error("Cannot save activity data without a selected organization");
            }

            const config = { params: { organization_id: selectedCompany.id } };

            return activityData?.id === undefined ?
                    await loggedInApiCall.post(baseUrl, activityData, config)
                :   await loggedInApiCall.put(`${baseUrl}/${activityData.id}`, activityData, config);
        },
        onSuccess: () => {
            const { productId } = options;

            if (productId === undefined) return;

            queryClient.invalidateQueries([getOneProductKey, productId.toString()]);
            queryClient.invalidateQueries([getProductScoresKey, productId]);
        },
    });
};

export const ProductOrganizationActivityDataWriteSchema = z.object({
    id: z.number().optional(),
    productOrganization: z.string().optional(),
    campaign: z.string().optional(),
    quantity: z.number().nullable().optional(),
});

export type ProductOrganizationActivityDataWrite = z.infer<typeof ProductOrganizationActivityDataWriteSchema>;
